
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdCheckCircle, MdInfo, MdArrowBack, MdSearch } from 'react-icons/md';
import axios from 'axios';
import Swal from 'sweetalert2';
import { GET_VENDOR_RESPONSE_BY_ID_API, UPDATE_VENDORS_RATE_API, SEND_FOR_COMPARE_VENDORS_API } from '../config/API';
import './VendorQuotesDetail.css';
import '../pages/Dashboard.css';
import Loader from '../components/common/Loader';

const VendorQuotesDetail = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();

    const [currentRequest, setCurrentRequest] = useState(null);
    const [currentQuotes, setCurrentQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingVendorId, setUpdatingVendorId] = useState(null);
    const [submittingAll, setSubmittingAll] = useState(false);
    const [bulkUpdating, setBulkUpdating] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const getLowestRate = (reqItemId) => {
        const rates = currentQuotes.map(quote => {
            const item = quote.items.find(i => i.reqItemId === reqItemId);
            return item ? (parseFloat(item.rate) || 0) : 0;
        }).filter(rate => rate > 0);
        
        return rates.length ? Math.min(...rates) : 0;
    };

    useEffect(() => {
        if (requestId) {
            fetchQuotationDetail();
        }
    }, [requestId, searchTerm]);

    const fetchQuotationDetail = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_VENDOR_RESPONSE_BY_ID_API(requestId), {
                params: { search: searchTerm },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                const data = response.data.requestData;

                if (data) {
                    setCurrentRequest({
                        id: data.id,
                        requestNumber: data.reqNumber || 'N/A',
                        grandTotal: data.grandTotal || '0.00',
                        tillDate: data.date,
                        status: data.status || 'pending'
                    });

                    const requestVendors = data.RequestVendors || [];
                    const requestItems = data.RequestItems || [];

                    // Map vendors to cards
                    const formattedQuotes = requestVendors.map(rv => {
                        const vendorInfo = rv.Vendor || {};
                        const responses = rv.VendorsRequests || [];

                        // Show all items requested for this request, with rates if quoted by this vendor
                        const vendorItems = requestItems.map(reqItem => {
                            const vendorRes = responses.find(res => res.reqItemId === reqItem.id);

                            const itemRate = vendorRes ? (parseFloat(vendorRes.rate) || 0) : 0;
                            const itemQty = parseFloat(reqItem.qty) || 0;
                            const itemTotal = vendorRes ? (parseFloat(vendorRes.total) || 0) : (itemRate * itemQty);

                            return {
                                id: vendorRes?.id,
                                reqItemId: reqItem.id,
                                itemName: reqItem.Product?.productName || reqItem.productDescription || 'Unknown Item',
                                unit: reqItem.Unit?.unitName || '-',
                                size: reqItem.size || '-',
                                qty: itemQty,
                                rate: itemRate || '', // Keep as empty string if 0 for better UX
                                total: itemTotal
                            };
                        });

                        const grandTotal = vendorItems.reduce((sum, item) => sum + item.total, 0);

                        return {
                            reqVendorId: rv.id,
                            vendorId: rv.vendorId,
                            vendorName: vendorInfo.vendorName || 'Unknown Vendor',
                            items: vendorItems,
                            grandTotal: grandTotal
                        };
                    });

                    setCurrentQuotes(formattedQuotes);
                } else {
                    setCurrentQuotes([]);
                }
            }
        } catch (error) {
            console.error("Error fetching quotation details:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRateChange = (reqVendorId, itemIndex, newRate) => {
        const updatedQuotes = currentQuotes.map(quote => {
            if (quote.reqVendorId === reqVendorId) {
                const updatedItems = [...quote.items];
                const numericRate = parseFloat(newRate) || 0;

                updatedItems[itemIndex] = {
                    ...updatedItems[itemIndex],
                    rate: newRate, // Store as string for input
                    total: numericRate * updatedItems[itemIndex].qty
                };

                // Recalculate Grand Total
                const newGrandTotal = updatedItems.reduce((sum, item) => sum + item.total, 0);

                return { ...quote, items: updatedItems, grandTotal: newGrandTotal };
            }
            return quote;
        });
        setCurrentQuotes(updatedQuotes);
    };

    const handleUpdateQuote = async (quote) => {
        const result = await Swal.fire({
            title: 'Update Rates?',
            text: `Are you sure you want to update rates for ${quote.vendorName}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setUpdatingVendorId(quote.reqVendorId);
                const token = localStorage.getItem('accessToken');

                const payload = {
                    vendors: [
                        {
                            reqVendorId: quote.reqVendorId,
                            reqItemId: quote.items.map(item => item.reqItemId),
                            rate: quote.items.map(item => item.rate)
                        }
                    ],
                    // status: 'submitted'
                };

                const response = await axios.post(UPDATE_VENDORS_RATE_API, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: `Rates for ${quote.vendorName} updated successfully.`,
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    Swal.fire('Failed', response.data.message || 'Failed to update rates.', 'error');
                }
            } catch (error) {
                console.error('Rate Update Error:', error);
                Swal.fire('Error', error.response?.data?.message || 'An error occurred while updating rates.', 'error');
            } finally {
                setUpdatingVendorId(null);
            }
        }
    };

    const handleBulkUpdateRates = async () => {
        const result = await Swal.fire({
            title: 'Update All Rates?',
            text: 'Are you sure you want to update individual rates for ALL vendors at once?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update All',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setBulkUpdating(true);
                const token = localStorage.getItem('accessToken');

                const payload = {
                    vendors: currentQuotes.map(quote => ({
                        reqVendorId: quote.reqVendorId,
                        reqItemId: quote.items.map(item => item.reqItemId),
                        rate: quote.items.map(item => item.rate)
                    }))
                };

                const response = await axios.post(UPDATE_VENDORS_RATE_API, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'All Rates Updated!',
                        text: 'Individual rates for all vendors have been successfully updated.',
                        timer: 2500,
                        showConfirmButton: false
                    });
                } else {
                    Swal.fire('Failed', response.data.message || 'Failed to update rates.', 'error');
                }
            } catch (error) {
                console.error('Bulk Rate Update Error:', error);
                Swal.fire('Error', error.response?.data?.message || 'An error occurred while updating rates.', 'error');
            } finally {
                setBulkUpdating(false);
            }
        }
    };

    const handleSendAllForCompare = async () => {
        const result = await Swal.fire({
            title: 'Send All Quotations?',
            text: 'This will submit updated rates for ALL vendors to the comparison system.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Send All',
            cancelButtonColor: '#10b981',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setSubmittingAll(true);
                const token = localStorage.getItem('accessToken');

                const payload = {
                    vendors: currentQuotes.map(quote => quote.reqVendorId)
                };

                const response = await axios.post(SEND_FOR_COMPARE_VENDORS_API, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    await Swal.fire({
                        icon: 'success',
                        title: 'Success!',
                        text: 'All vendor quotations have been successfully submitted for comparison.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                    navigate('/compare-po');
                } else {
                    Swal.fire('Failed', response.data.message || 'Failed to submit quotations.', 'error');
                }
            } catch (error) {
                console.error('Send All Error:', error);
                Swal.fire(
                    'Error',
                    error.response?.data?.error || 'An error occurred while submitting quotations.',
                    'error'
                );
            } finally {
                setSubmittingAll(false);
            }
        }
    };

    if (loading) {
        return (
            <Loader text="Fetching quotations..." />
        );
    }

    if (!currentRequest) {
        return (
            <div className="dashboard">
                <div className="p-8 text-center">
                    <h3>Request not found</h3>
                    <button onClick={() => navigate('/view-quotations')} className="view-quote-btn">Back to List</button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard vendor-quotes-detail-page">
            <div className="detail-header-nav">
                <button onClick={() => navigate('/view-quotations')} className="back-btn">
                    <MdArrowBack />
                </button>

            </div>

            <div className="comparison-container">
                <div className="vendor-grid-view fade-in">
                    <h2 className="section-title">Vendor Quotations</h2>
                    <div className="detail-view-header-view-quote" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                        <div className="header-info-center">
                            <h3 className="section-title po-title">#{currentRequest.requestNumber?.toUpperCase()}</h3>
                        </div>
                        <div className="header-spacer"></div>
                    </div>
                    {currentQuotes.length === 0 ? (
                        <div className="card-container no-data-card no-data-container">

                            <MdInfo size={40} color="#9ca3af" />
                            <p className="no-data-text">No vendor quotations found for this request.</p>
                        </div>
                    ) : (
                        <>
                            <div className="comparison-grid">
                                {currentQuotes.map((quote) => (
                                    <div key={quote.reqVendorId} className="quote-card">
                                        <div className="quote-header">
                                            <div className="vendor-info">
                                                <h3>{quote.vendorName}</h3>
                                            </div>
                                            <div className="grand-total-highlight">
                                                <span className="label">Grand Total</span>
                                                <span className="value">₹{quote.grandTotal.toLocaleString()}</span>
                                            </div>
                                        </div>

                                        <div className="items-breakdown">
                                            <table className="comparison-items-table">
                                                <thead>
                                                    <tr>
                                                        <th>Item</th>
                                                        <th style={{ textAlign: 'center' }}>Unit</th>
                                                        <th style={{ textAlign: 'center' }}>Size</th>
                                                        <th style={{ textAlign: 'center' }}>Qty</th>
                                                        <th style={{ width: '100px', textAlign: 'center' }}>Rate</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {quote.items.map((item, idx) => {
                                                        const numericRate = parseFloat(item.rate) || 0;
                                                        const lowestRate = getLowestRate(item.reqItemId);
                                                        const isLowest = numericRate > 0 && numericRate === lowestRate;
                                                        const difference = (numericRate > 0 && numericRate > lowestRate) ? (numericRate - lowestRate) : 0;

                                                        return (
                                                        <tr key={idx}>
                                                            <td>{item.itemName}</td>
                                                            <td style={{ textAlign: 'center' }}>{item.unit}</td>
                                                            <td style={{ textAlign: 'center' }}>{item.size}</td>
                                                            <td style={{ textAlign: 'center' }}>{item.qty}</td>
                                                            <td>
                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control rate-input ${isLowest ? 'lowest-rate-input' : ''}`}
                                                                        value={item.rate}
                                                                        onChange={(e) => handleRateChange(quote.reqVendorId, idx, e.target.value)}
                                                                        min="0"
                                                                        style={isLowest ? { borderColor: '#22c55e', backgroundColor: '#f0fdf4', color: '#16a34a', fontWeight: 'bold' } : {}}
                                                                    />
                                                                    {!isLowest && numericRate > 0 && lowestRate > 0 && (
                                                                        <span style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px', fontWeight: '500' }}>
                                                                            (+₹{difference.toLocaleString('en-US', { minimumFractionDigits: 2 })})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="quote-actions">
                                            <button
                                                className="approve-quote-btn"
                                                onClick={() => handleUpdateQuote(quote)}
                                                disabled={updatingVendorId === quote.reqVendorId}
                                            >
                                                {updatingVendorId === quote.reqVendorId ? 'Updating...' : 'Update Individual'}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="footer-actions-details" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
                                {currentRequest.status?.toLowerCase() === 'allresponsereceived' ? (
                                    <button
                                        className={`approve-btn-large ${submittingAll ? 'loading' : ''}`}
                                        onClick={handleSendAllForCompare}
                                        disabled={submittingAll || bulkUpdating}
                                    >
                                        {submittingAll ? 'Sending...' : 'Send All for Compare'} <MdCheckCircle size={20} style={{ marginLeft: '0.5rem' }} />
                                    </button>
                                ) : (
                                    <button
                                        className={`bulk-update-btn ${bulkUpdating ? 'loading' : ''}`}
                                        onClick={handleBulkUpdateRates}
                                        disabled={bulkUpdating || submittingAll}
                                        style={{
                                            background: '#4f46e5',
                                            color: 'white',
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '8px',
                                            border: 'none',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            opacity: (bulkUpdating || submittingAll) ? 0.7 : 1
                                        }}
                                    >
                                        {bulkUpdating ? 'Updating...' : 'Update All Rates'} <MdCheckCircle size={20} />
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VendorQuotesDetail;
